import { IconNames } from '@blueprintjs/icons';
import { BenefitTypeDisplayTextMap } from '@strata/constants';
import { Button } from '@strata/react-buttons';
import { GridWithSearch, PrimaryData, SecondaryData } from '@strata/react-grid';
import { Icon } from '@strata/react-status-icons';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

import { isCapabilityActive } from 'services/CapabilityHelpers';
import { CREATE_ROUTE } from 'services/constants';
import { handleError } from 'services/ErrorManagement';
import { get, useGet } from 'services/Http';
import { getPayerMap, getPayerSegmentMap } from 'services/PayerSegmentSupport';

const columns = ({ navigate, npi, location }) => [
    {
        accessorKey: 'payer',
        header: 'Payer',
        size: 125,
        cell: ({ getValue }) => <PrimaryData data={getValue()} />,
    },
    {
        accessorKey: 'segment',
        header: 'Payer Segment',
        size: 125,
    },
    {
        accessorKey: 'benefit',
        header: 'Benefit',
        size: 80,
        cell: ({ getValue }) => <SecondaryData data={BenefitTypeDisplayTextMap[getValue()]} />,
    },
    {
        accessorKey: 'no_market',
        header: 'Live Referral Events',
        size: 80,
        cell: ({ getValue }) => <SecondaryData data={getValue() ? 'No' : 'Yes'} />,
    },
    {
        accessorKey: 'upn',
        header: 'Last Modified By',
        size: 120,
    },
    {
        accessorKey: 'actions',
        header: 'Actions',
        enableSorting: false,
        size: 70,
        cell: ({ row }) => (
            <div className="sds_filter-list__actions">
                <Icon
                    icon={IconNames.EDIT}
                    intent={Icon.Intent.SECONDARY}
                    title="Edit"
                    onClick={() =>
                        navigate({
                            pathname: `/edit-supplier/${npi}/${row.original.payer_id}`,
                            hash: location.hash,
                        })
                    }
                />
            </div>
        ),
    },
];

export const PayerCapabilityList = () => {
    const { npi } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [payerCapabilities, setPayerCapabilities] = useState();
    useEffect(() => {
        void (async () => {
            try {
                setPayerCapabilities(
                    (await get(`/reference/capabilities/${npi}`))?.payer_capabilities,
                );
            } catch (err) {
                handleError('Error fetching payer capabilities', err);
            }
        })();
    }, [npi]);

    const payers = useGet('/reference/payers/');
    const payerMap = getPayerMap(payers);
    const payerSegments = useGet('/reference/payer_segments');
    const payerSegmentMap = getPayerSegmentMap(payerSegments);

    const isLoading = !(payerCapabilities && payers && payerSegments);

    const activePayerCapabilities = useMemo(
        () =>
            payerCapabilities?.filter(isCapabilityActive).reduce((prev, curr) => {
                const existingRowIndex = prev.findIndex(
                    (row) =>
                        row.payer_id === curr.payer_id &&
                        row.segment_id === curr.segment_id &&
                        row.benefit === curr.benefit,
                );
                return existingRowIndex === -1
                    ? [
                          ...prev,
                          {
                              payer_id: curr.payer_id,
                              payer: `${payerMap?.[curr.payer_id]} - ${curr.payer_id}`,
                              segment_id: curr.segment_id,
                              segment: payerSegmentMap?.[curr.segment_id]?.segment_name,
                              benefit: curr.benefit,
                              no_market: curr.no_market,
                              upn: curr.upn,
                              last_updated: curr.last_updated,
                          },
                      ]
                    : prev.map((row, i) =>
                          i !== existingRowIndex
                              ? row
                              : {
                                    ...row,
                                    no_market: curr.no_market ? curr.no_market : row.no_market, // If at least 1 capability for the segment/benefit is no_market show as No
                                    last_updated: dayjs(row.last_updated).isAfter(curr.last_updated)
                                        ? row.last_updated
                                        : curr.last_updated,
                                    upn: dayjs(row.last_updated).isAfter(curr.last_updated)
                                        ? row.upn
                                        : curr.upn,
                                },
                      );
            }, []),
        [payerCapabilities, payerMap, payerSegmentMap],
    );

    /** Count of unique payers supplier has capabilities for */
    const activePayerCount = new Set(
        activePayerCapabilities?.map((capability) => capability.payer_id),
    ).size;

    return (
        <GridWithSearch
            data={activePayerCapabilities}
            isLoading={isLoading}
            sortProperties={[{ id: 'payer', desc: false }]}
            rightHeaderContent={
                !isLoading && activePayerCount < payers.length ? (
                    <Button
                        label="Add Payer Capability"
                        intent={Button.Intent.PRIMARY}
                        onClick={() =>
                            navigate({
                                pathname: `/edit-supplier/${npi}/${CREATE_ROUTE}`,
                                hash: location.hash,
                            })
                        }
                    />
                ) : undefined
            }
            columns={columns({ navigate, npi, location })}
        />
    );
};
